import { useState } from 'react';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import type { WorkOrder } from '@/types/maintenance';

interface Technician { id: string; name: string; }

interface Props {
  workOrders: WorkOrder[];
  technicians: Technician[];
}

export function CapacityPanel({ workOrders, technicians }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { settings } = useOrgSettings();
  const defaultHours = settings.default_cm_duration_minutes / 60;
  const hoursPerTech = settings.hours_per_tech;

  const totalAvailable = technicians.length * hoursPerTech;

  const inProgress = workOrders.filter(wo => wo.status === 'in_progress');
  const requested = workOrders.filter(wo => wo.status === 'requested');

  function estimateWOHours(wo: WorkOrder): number {
    if (wo.type === 'preventive' && wo.mold?.typical_pm_duration_minutes) {
      return wo.mold.typical_pm_duration_minutes / 60;
    }
    return defaultHours;
  }

  const inProgressHours = inProgress.reduce((sum, wo) => {
    if (wo.labor_minutes && wo.labor_minutes > 0) return sum + wo.labor_minutes / 60;
    // Estimate based on time since start, capped at WO type estimate
    if (wo.start_time) {
      const hrs = (Date.now() - new Date(wo.start_time).getTime()) / 3600000;
      return sum + Math.min(hrs, estimateWOHours(wo));
    }
    return sum + estimateWOHours(wo);
  }, 0);

  const readyHours = requested.reduce((sum, wo) => sum + estimateWOHours(wo), 0);

  const utilization = totalAvailable > 0 ? Math.round((inProgressHours / totalAvailable) * 100) : 0;
  const backlogDays = totalAvailable > 0 ? (readyHours / totalAvailable).toFixed(1) : '0.0';

  const totalDemand = inProgressHours + readyHours;
  const overCapacity = totalDemand > totalAvailable;

  const barMax = Math.max(totalAvailable, totalDemand);
  const inProgressPct = (inProgressHours / barMax) * 100;
  const readyPct = (readyHours / barMax) * 100;
  const availablePct = totalAvailable > totalDemand ? ((totalAvailable - totalDemand) / barMax) * 100 : 0;

  // Technician breakdown — hours-based
  const activeTechIds = new Set(inProgress.filter(wo => wo.assigned_to).map(wo => wo.assigned_to!));
  const activeTechs = technicians.filter(t => activeTechIds.has(t.id));
  const idleTechs = technicians.filter(t => !activeTechIds.has(t.id));

  const techEstimatedHours = new Map<string, number>();
  const techWOCount = new Map<string, number>();
  inProgress.forEach(wo => {
    if (!wo.assigned_to) return;
    const hrs = estimateWOHours(wo);
    techEstimatedHours.set(wo.assigned_to, (techEstimatedHours.get(wo.assigned_to) || 0) + hrs);
    techWOCount.set(wo.assigned_to, (techWOCount.get(wo.assigned_to) || 0) + 1);
  });
  // Overloaded = estimated hours exceed available hours per tech
  const overloadedTechs = technicians.filter(t => (techEstimatedHours.get(t.id) || 0) > hoursPerTech);

  return (
    <div className="bg-card rounded-lg border border-border/50 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">Maintenance Capacity Today</h2>
        </div>
        {overCapacity && (
          <span className="text-[11px] font-medium text-[hsl(var(--status-red))] bg-[hsl(var(--status-red-bg))] px-2 py-0.5 rounded">
            Over Capacity
          </span>
        )}
      </div>

      {/* Stacked bar */}
      <div className="h-5 rounded-full bg-muted flex overflow-hidden mb-4">
        <div
          className="bg-molten transition-all"
          style={{ width: `${inProgressPct}%` }}
          title={`In Progress: ${Math.round(inProgressHours)}h`}
        />
        <div
          className="bg-muted-foreground/30 transition-all"
          style={{ width: `${readyPct}%` }}
          title={`Ready: ${Math.round(readyHours)}h`}
        />
        {overCapacity && (
          <div
            className="bg-[hsl(var(--status-red)/0.2)] transition-all"
            style={{ width: `${((totalDemand - totalAvailable) / barMax) * 100}%` }}
          />
        )}
        {availablePct > 0 && (
          <div className="flex-1" />
        )}
      </div>

      {/* Metrics row */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <Metric label="Available" value={`${totalAvailable}h`} />
        <Metric label="In Progress" value={`${Math.round(inProgressHours)}h`} color="text-molten-deep" />
        <Metric label="Ready" value={`${Math.round(readyHours)}h`} />
        <Metric label="Utilization" value={`${utilization}%`} color={utilization > 85 ? 'text-[hsl(var(--status-red))]' : utilization > 60 ? 'text-[hsl(var(--status-amber))]' : undefined} />
        <Metric label="Backlog" value={`${backlogDays}d`} color={Number(backlogDays) > 1 ? 'text-[hsl(var(--status-red))]' : undefined} />
      </div>

      {/* Expandable detail */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 mt-3 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? 'Hide details' : 'Technician breakdown'}
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-3 gap-4 text-xs">
          <div>
            <div className="font-medium text-muted-foreground mb-1.5">Active ({activeTechs.length})</div>
            {activeTechs.map(t => (
              <div key={t.id} className="flex items-center gap-1.5 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-molten" />
                <span>{t.name}</span>
                <span className="text-muted-foreground/60 ml-auto">
                  {(techEstimatedHours.get(t.id) || 0).toFixed(1)}h est
                </span>
              </div>
            ))}
            {activeTechs.length === 0 && <span className="text-muted-foreground/50">None</span>}
          </div>
          <div>
            <div className="font-medium text-muted-foreground mb-1.5">Idle ({idleTechs.length})</div>
            {idleTechs.map(t => (
              <div key={t.id} className="flex items-center gap-1.5 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                <span>{t.name}</span>
              </div>
            ))}
            {idleTechs.length === 0 && <span className="text-muted-foreground/50">None</span>}
          </div>
          <div>
            <div className={cn('font-medium mb-1.5', overloadedTechs.length > 0 ? 'text-[hsl(var(--status-red))]' : 'text-muted-foreground')}>
              Overloaded ({overloadedTechs.length})
            </div>
            {overloadedTechs.map(t => (
              <div key={t.id} className="flex items-center gap-1.5 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-red))]" />
                <span>{t.name}</span>
                <span className="text-[hsl(var(--status-red))] ml-auto">
                  {(techEstimatedHours.get(t.id) || 0).toFixed(1)}h est
                </span>
              </div>
            ))}
            {overloadedTechs.length === 0 && <span className="text-muted-foreground/50">None</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}:</span>
      <span className={cn('font-semibold', color || 'text-foreground')}>{value}</span>
    </div>
  );
}
